/* eslint-disable @typescript-eslint/no-unused-vars, react-hooks/exhaustive-deps, import/no-anonymous-default-export, no-useless-escape */
/**
 * Shared print utilities — hospital letterhead header + footer for all reports
 */

export interface HospitalInfo {
  name: string;
  address: string;
  phone: string;
  email: string;
  regNo: string;
  gstin?: string;
}

// Cache hospital info after first fetch
let _hospitalInfo: HospitalInfo | null = null;

const API_BASE = process.env.REACT_APP_API_URL ;

export async function getHospitalInfo(): Promise<HospitalInfo> {
  if (_hospitalInfo) return _hospitalInfo;
  try {
    const r = await fetch(`${API_BASE}/settings`, { headers: { Authorization: `Bearer ${localStorage.getItem('accessToken')}` } });
    const settings = await r.json();
    const get = (key: string) => {
      const entry = Array.isArray(settings) ? settings.find((s: any) => s.key === key) : settings[key];
      if (!entry) return '';
      const val = entry.value ?? entry;
      return typeof val === 'string' ? val.replace(/^"|"$/g, '') : String(val);
    };
    _hospitalInfo = {
      name: get('hospital_name') || 'City Hospital & Research Centre',
      address: get('hospital_address') || '',
      phone: get('hospital_phone') || '',
      email: get('hospital_email') || '',
      regNo: get('hospital_reg_no') || '',
      gstin: get('hospital_gstin') || '',
    };
  } catch {
    _hospitalInfo = { name: 'City Hospital & Research Centre', address: '', phone: '', email: '', regNo: '' };
  }
  return _hospitalInfo!;
}

export function printHeaderHtml(h: HospitalInfo, docTitle: string): string {
  return `
    <div class="print-header">
      <div class="hospital-name">${h.name}</div>
      <div class="hospital-sub">${[h.address, h.phone, h.email].filter(Boolean).join(' | ')}</div>
      ${h.regNo ? `<div class="hospital-meta">Reg No: ${h.regNo}${h.gstin ? ' | GSTIN: ' + h.gstin : ''}</div>` : ''}
      <div class="doc-title">${docTitle}</div>
      <hr class="header-line"/>
    </div>
  `;
}

export function printFooterHtml(note?: string): string {
  const now = new Date().toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' });
  return `
    <div class="print-footer">
      <hr class="footer-line"/>
      <div class="footer-row">
        <span>${note || 'This is a computer-generated document.'}</span>
        <span>Printed: ${now}</span>
      </div>
      <div class="footer-sig">
        <div class="sig-box"><div class="sig-line"></div><div>Authorised Signatory</div></div>
      </div>
    </div>
  `;
}

export function printBaseCss(headerColor = '#1e3a5f'): string {
  return `
  @media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }
  * { box-sizing: border-box; margin: 0; padding: 0; font-family: Arial, sans-serif; }
  body { font-size: 11px; color: #111; background: #fff; padding: 18px 22px; }
  .print-header { text-align: center; margin-bottom: 10px; }
  .header-top-row { display: flex; align-items: center; justify-content: center; gap: 10px; }
  .header-logo { height: 40px; }
  .nabl-badge { display: inline-flex; align-items: center; gap: 4px; border: 1.5px solid ${headerColor}; border-radius: 4px; padding: 2px 8px; font-size: 8px; font-weight: 800; color: ${headerColor}; letter-spacing: 0.5px; }
  .hospital-name { font-size: 18px; font-weight: 700; color: ${headerColor}; letter-spacing: 0.5px; }
  .hospital-sub { font-size: 10px; color: #555; margin-top: 2px; }
  .hospital-meta { font-size: 9px; color: #888; margin-top: 2px; }
  .doc-title { font-size: 13px; font-weight: 700; margin: 8px 0 4px; text-transform: uppercase; letter-spacing: 1px; color: ${headerColor}; }
  .header-line { border: none; border-top: 2px solid ${headerColor}; margin: 4px 0 12px; }
  .id-strip { display: flex; align-items: center; justify-content: space-between; margin-bottom: 10px; gap: 12px; }
  .id-codes { display: flex; align-items: center; gap: 14px; }
  .print-footer { margin-top: 24px; }
  .footer-line { border: none; border-top: 1px solid #ccc; margin-bottom: 6px; }
  .footer-row { display: flex; justify-content: space-between; font-size: 9px; color: #666; }
  .footer-sig { margin-top: 30px; display: flex; justify-content: flex-end; }
  .sig-box { text-align: center; font-size: 10px; color: #555; }
  .sig-line { border-top: 1px solid #333; width: 150px; margin-bottom: 3px; }
  table, .data-table { width: 100%; border-collapse: collapse; margin: 8px 0; }
  th { background: ${headerColor}; color: #fff; padding: 5px 8px; font-size: 10px; text-align: left; }
  td { padding: 4px 8px; border-bottom: 1px solid #eee; font-size: 10px; }
  tr:nth-child(even) td { background: #f9fafb; }
  .label { font-weight: 600; color: #444; }
  .section { margin: 10px 0; }
  .section h4, .section-title { font-size: 11px; font-weight: 700; color: ${headerColor}; border-bottom: 1px solid #ddd; padding-bottom: 3px; margin-bottom: 6px; text-transform: uppercase; }
  .data-val { font-size: 10px; color: #222; margin-bottom: 6px; white-space: pre-wrap; }
  .row { display: flex; gap: 12px; margin: 4px 0; }
  .field { flex: 1; }
  .field .lbl { font-size: 9px; color: #888; text-transform: uppercase; }
  .field .val { font-size: 11px; font-weight: 600; }
  .badge { display: inline-block; padding: 1px 8px; border-radius: 9999px; font-size: 9px; font-weight: 700; }
  .badge-blue { background: #dbeafe; color: #1d4ed8; }
  .badge-green { background: #dcfce7; color: #15803d; }
  .badge-red { background: #fee2e2; color: #dc2626; }
  .badge-amber { background: #fef3c7; color: #d97706; }
  .watermark { position: fixed; bottom: 40%; left: 50%; transform: translateX(-50%) rotate(-30deg); font-size: 60px; color: rgba(0,0,0,0.04); pointer-events: none; z-index: 0; font-weight: 900; white-space: nowrap; }
`;
}

// Kept for backward compatibility — the module-level default palette.
export const PRINT_BASE_CSS = printBaseCss();

/** Deterministic bar-pattern visual for "show barcode" — a print-friendly identifier strip, not a scannable 1D standard. */
export function renderBarcodeStrip(text: string): string {
  if (!text) return '';
  const bars = Array.from(text).map((ch, i) => {
    const w = 1 + (ch.charCodeAt(0) + i) % 3; // 1-3px, deterministic per character
    return `<div style="display:inline-block;width:${w}px;height:32px;background:${i % 2 === 0 ? '#111' : '#fff'};"></div>`;
  }).join('');
  return `<div style="display:flex;align-items:flex-end;">${bars}</div><div style="font-size:8px;letter-spacing:2px;font-family:monospace;margin-top:2px;">${text}</div>`;
}

async function getQrDataUrl(text: string): Promise<string> {
  try {
    const QR = await import('qrcode');
    return await QR.toDataURL(text, { width: 90, margin: 1 });
  } catch { return ''; }
}

export interface ReportTemplate {
  template_name?: string;
  header_color?: string;
  show_logo?: boolean;
  show_nabl_logo?: boolean;
  show_barcode?: boolean;
  show_qr?: boolean;
  show_watermark?: boolean;
  watermark_text?: string;
  footer_text?: string;
  paper_size?: string;
  orientation?: string;
}

/** Best-matching active report template for a department/report type, fetched via the report-master API. Pass an `api` client to avoid circular imports. */
export async function findReportTemplate(api: any, department: string | undefined, reportType: string): Promise<ReportTemplate | null> {
  try {
    const r = await api.getLabReportTemplates?.({ type: reportType, department });
    const list: any[] = Array.isArray(r?.data) ? r.data : [];
    if (list.length === 0 && department) {
      const r2 = await api.getLabReportTemplates?.({ type: reportType });
      return (Array.isArray(r2?.data) && r2.data[0]) || null;
    }
    return list[0] || null;
  } catch { return null; }
}

async function assembleHtml(contentHtml: string, docTitle: string, noteText: string | undefined, tmpl: ReportTemplate | null, barcodeText?: string): Promise<string> {
  const h = await getHospitalInfo();
  const headerColor = tmpl?.header_color || '#1e3a5f';
  const paperSize = (tmpl?.paper_size || 'A4').toUpperCase();
  const orientation = (tmpl?.orientation || 'PORTRAIT').toLowerCase();
  const showWatermark = tmpl ? tmpl.show_watermark !== false : true;
  const watermarkText = tmpl?.watermark_text || h.name;
  const showLogo = tmpl?.show_logo !== false;
  const footerNote = tmpl?.footer_text || noteText || 'This is a computer-generated document.';

  let idStrip = '';
  if (barcodeText && (tmpl?.show_barcode || tmpl?.show_qr)) {
    const parts: string[] = [];
    if (tmpl?.show_barcode) parts.push(renderBarcodeStrip(barcodeText));
    if (tmpl?.show_qr) { const qr = await getQrDataUrl(barcodeText); if (qr) parts.push(`<img src="${qr}" style="width:60px;height:60px;"/>`); }
    idStrip = `<div class="id-strip"><div class="id-codes">${parts.join('')}</div></div>`;
  }

  const nablBadge = tmpl?.show_nabl_logo ? `<span class="nabl-badge">✓ NABL ACCREDITED</span>` : '';
  const logoImg = showLogo ? `<img class="header-logo" src="/logo.png" onerror="this.style.display='none'"/>` : '';

  return `<!DOCTYPE html><html><head><title>${docTitle} - ${h.name}</title>
    <style>${printBaseCss(headerColor)} @page { size: ${paperSize} ${orientation}; margin: 14mm; }</style></head>
    <body>
      ${showWatermark ? `<div class="watermark">${watermarkText}</div>` : ''}
      <div class="print-header">
        <div class="header-top-row">${logoImg}${nablBadge}</div>
        <div class="hospital-name">${h.name}</div>
        <div class="hospital-sub">${[h.address, h.phone, h.email].filter(Boolean).join(' | ')}</div>
        ${h.regNo ? `<div class="hospital-meta">Reg No: ${h.regNo}${h.gstin ? ' | GSTIN: ' + h.gstin : ''}</div>` : ''}
        <div class="doc-title">${docTitle}</div>
        <hr class="header-line"/>
      </div>
      ${idStrip}
      ${contentHtml}
      ${printFooterHtml(footerNote)}
      <script>window.onload=()=>{window.print();setTimeout(()=>window.close(),800);}<\/script>
    </body></html>`;
}

export async function openPrintWindow(contentHtml: string, docTitle: string, noteText?: string): Promise<void> {
  const html = await assembleHtml(contentHtml, docTitle, noteText, null);
  const w = window.open('', '_blank', 'width=900,height=1100');
  if (w) { w.document.write(html); w.document.close(); }
}

/**
 * Same as openPrintWindow, but looks up the active Report Master template for the given
 * department/reportType and applies its paper size, header color, NABL badge, barcode/QR,
 * watermark and footer text — falling back to the plain defaults when no template exists.
 */
export async function openTemplatedPrintWindow(opts: {
  api: any; contentHtml: string; docTitle: string; reportType: string;
  department?: string; barcodeText?: string; noteText?: string;
}): Promise<void> {
  const tmpl = await findReportTemplate(opts.api, opts.department, opts.reportType);
  const html = await assembleHtml(opts.contentHtml, opts.docTitle, opts.noteText, tmpl, opts.barcodeText);
  const w = window.open('', '_blank', 'width=900,height=1100');
  if (w) { w.document.write(html); w.document.close(); }
}
